import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { ProtectedRoute } from "./ProtectedRoute";
import { AppShell } from "../layouts/AppShell";
import { Permission } from "../permissions";

import LoginPage from "../../modules/authentication/pages/LoginPage";
import DashboardPage from "../../modules/dashboard/pages/DashboardPage";
import OrdersPage from "../../modules/orders/pages/OrdersPage";
import OrderDetailPage from "../../modules/orders/pages/OrderDetailPage";
import OrderWizardPage from "../../modules/orders/pages/OrderWizardPage";
import ProductsPage from "../../modules/products/pages/ProductsPage";
import AvailabilityPage from "../../modules/availability/pages/AvailabilityPage";
import ProfilePage from "../../modules/profile/pages/ProfilePage";
import ConfirmOrderPage from "../../modules/orders/pages/ConfirmOrderPage";

// Admin-only routes (top-level, flat — no /admin prefix). Real pages
// come in A2+; A1.5 wires them all as Coming-soon placeholders.
import UsersPage from "../../modules/users/pages/UsersPage";
import UserDetailPage from "../../modules/users/pages/UserDetailPage";
import CategoriesPage from "../../modules/categories/pages/CategoriesPage";
import BrandsPage from "../../modules/brands/pages/BrandsPage";
import AddressesPage from "../../modules/addresses/pages/AddressesPage";
import DeliveryTimesPage from "../../modules/delivery-times/pages/DeliveryTimesPage";
import HolidaysPage from "../../modules/holidays/pages/HolidaysPage";
// Admin + warehouse routes
import CollectionPage from "../../modules/collection/pages/CollectionPage";
import DeliveryPage from "../../modules/delivery/pages/DeliveryPage";
import DamagesPage from "../../modules/damages/pages/DamagesPage";
// Admin-only reporting
import ReportsPage from "../../modules/reports/pages/ReportsPage";

/**
 * Flat top-level admin routes. Each is gated by its own permission.
 * The `permission` set stays as-is so existing tests keep passing.
 */
const FLAT_ADMIN_ROUTES = [
  { path: "/users", Comp: UsersPage, permission: Permission.ADMIN_USERS_MANAGE },
  { path: "/users/:id", Comp: UserDetailPage, permission: Permission.ADMIN_USERS_MANAGE },
  { path: "/categories", Comp: CategoriesPage, permission: Permission.ADMIN_CATEGORIES_MANAGE },
  { path: "/brands", Comp: BrandsPage, permission: Permission.ADMIN_BRANDS_MANAGE },
  { path: "/addresses", Comp: AddressesPage, permission: Permission.ADMIN_ADDRESSES_MANAGE },
  { path: "/delivery-times", Comp: DeliveryTimesPage, permission: Permission.ADMIN_DELIVERY_TIMES_MANAGE },
  { path: "/holidays", Comp: HolidaysPage, permission: Permission.ADMIN_PUBLIC_HOLIDAYS_MANAGE },
  { path: "/collection", Comp: CollectionPage, permission: Permission.ADMIN_COLLECTION_MANAGE },
  { path: "/delivery", Comp: DeliveryPage, permission: Permission.ADMIN_DELIVERY_MANAGE },
  { path: "/damages", Comp: DamagesPage, permission: [Permission.ADMIN_DAMAGES_MANAGE, Permission.DAMAGES_VIEW] },
  { path: "/reports", Comp: ReportsPage, permission: Permission.ADMIN_REPORTS_VIEW },
];

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/confirm/:token" element={<ConfirmOrderPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute permission={Permission.DASHBOARD_VIEW}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute permission={[Permission.ORDERS_VIEW_OWN, Permission.ORDERS_VIEW_ALL]}>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/new"
          element={
            <ProtectedRoute permission={Permission.ORDERS_CREATE}>
              <OrderWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id/edit"
          element={
            <ProtectedRoute permission={Permission.ORDERS_CREATE}>
              <OrderWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute permission={[Permission.ORDERS_VIEW_OWN, Permission.ORDERS_VIEW_ALL]}>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute permission={Permission.PRODUCTS_VIEW}>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/availability"
          element={
            <ProtectedRoute permission={Permission.AVAILABILITY_VIEW}>
              <AvailabilityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute permission={Permission.PROFILE_VIEW}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* ── Flat admin routes (A1.5 pivot). ─────────────────────── */}
        {FLAT_ADMIN_ROUTES.map(({ path, Comp, permission }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute permission={permission}>
                <Comp />
              </ProtectedRoute>
            }
          />
        ))}

        {/* ── Legacy /admin/* redirects (A1 → A1.5 pivot). ────────── */}
        {/* Explicit ones for common bookmarks. */}
        <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
        <Route path="/admin/orders" element={<Navigate to="/orders" replace />} />
        <Route path="/admin/products" element={<Navigate to="/products" replace />} />
        {/* Catch-all for any other /admin/xyz — send to /dashboard. */}
        <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
